import axios from 'axios';

import { COREAPI, getUserData } from '../constants/global';
import { type ApiResult, classifyResponse } from './api-result';
import {
	type ChunkOutcome,
	type CompleteOutcome,
	completeRelayUpload,
	getRelaySession,
	type IRelaySession,
	type IRelaySessionState,
	signRelay,
	uploadRelayChunk,
} from './media-relay';

/**
 * Client for api.core's result videos (`/api/v1/Election/{electionId}/result-videos`): an EC8A
 * agent's recording of the result sheet, added AFTER the result is submitted.
 *
 * Same discipline as `cancellation-api.ts` (whose classifiers it reuses): no 401→logout
 * interceptor, a 20 s timeout on api.core calls, and every status resolves so callers classify
 * the body instead of catching. The bytes go up through api.core's MediaStash relay
 * (`media-relay.ts`) — the browser never talks to the storage provider.
 */
const videoApi = axios.create({ baseURL: COREAPI, timeout: 20_000 });

videoApi.interceptors.request.use((config) => {
	const { token, user } = getUserData();
	if (token) config.headers.Authorization = `Bearer ${token}`;
	if (user?.PartyId) config.headers.PartyId = user.PartyId;
	return config;
});

videoApi.interceptors.response.use((response) => {
	if (typeof response.data === 'string' && response.data) {
		try {
			response.data = JSON.parse(response.data);
		} catch {
			// Not JSON — leave as-is (an HTML fallback page classifies as unreadable).
		}
	}
	return response;
});

// ---------------------------------------------------------------------------
// Contract (enums travel BY NAME)
// ---------------------------------------------------------------------------

export type ResultVideoPolicy = 'Off' | 'Optional' | 'Recommended' | 'Required';
export type CaptureMode = 'Live' | 'Uploaded';

export interface IResultVideoPolicy {
	electionId: number;
	videoPolicy: ResultVideoPolicy;
	source: 'party' | 'election' | 'default';
	maxDurationSec: number;
	maxBytes: number;
	windowHours: number;
	/** False when the server cannot store video — hide every affordance. */
	enabled: boolean;
}

export interface ISignVideoRequest {
	pollingUnitCode: string;
	mime: string;
	bytes: number;
	durationSec?: number;
	/** UUID minted with the local record — the server's idempotency key for the asset. */
	clientUploadId: string;
	fileName: string;
	sha256?: string;
}

export interface IVideoChapter {
	key: string;
	atSec: number;
}

export interface IRegisterVideoRequest {
	pollingUnitCode: string;
	resultReferenceId: string;
	storagePublicId: string;
	/** Optional and ignored since the MediaStash relay — the server derives it. */
	url?: string;
	mime: string;
	bytes: number;
	durationSec?: number;
	sha256?: string;
	captureMode: CaptureMode;
	captureStartedAt?: string;
	captureEndedAt?: string;
	latitude?: number;
	longitude?: number;
	accuracyMeters?: number;
	chapters?: IVideoChapter[];
	deviceInfo?: string;
}

export interface IResultVideo {
	id: number;
	pollingUnitCode: string;
	resultReferenceId: string;
	mime: string;
	bytes: number;
	durationSec?: number | null;
	captureMode: CaptureMode;
	captureStartedAt?: string | null;
	chapters: IVideoChapter[];
	ledgerConfirmed: boolean;
	registeredAt: string;
	supersededAt?: string | null;
	viewUrl: string;
	thumbnailUrl?: string | null;
	viewUrlExpiresAt: string;
	/** False for a video stored on the retired provider — `viewUrl` is then empty. Absent on older servers. */
	playable?: boolean;
}

export interface IResultVideoState {
	current: IResultVideo | null;
	history: IResultVideo[];
	windowClosesAt: string | null;
	windowOpen: boolean;
	windowClosedReason: 'expired' | 'reviewed' | null;
	videoPolicy: ResultVideoPolicy;
}

export const RESULT_VIDEO_CODES = {
	notConfigured: 'RESULT_VIDEO_NOT_CONFIGURED',
	off: 'RESULT_VIDEO_OFF',
	windowClosed: 'RESULT_VIDEO_WINDOW_CLOSED',
	belongsElsewhere: 'RESULT_VIDEO_BELONGS_ELSEWHERE',
	/** Register: the asset isn't confirmed in storage yet — retry with backoff. */
	storagePending: 'STORAGE_PENDING',
} as const;

/** What the client may assume when the server does not say (absent route, offline). */
export type PolicyOutcome = { mode: 'known'; policy: IResultVideoPolicy } | { mode: 'absent' } | { mode: 'unknown' };

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

const POLICIES: ResultVideoPolicy[] = ['Off', 'Optional', 'Recommended', 'Required'];

export const toPolicyOutcome = (status: number, body: unknown): PolicyOutcome => {
	// A 404 on the policy route is a server without the feature; so is the SPA's HTML 200.
	if (status === 404) return { mode: 'absent' };
	const res = classifyResponse(status, body, (data) =>
		isObject(data) && POLICIES.includes(data.videoPolicy as ResultVideoPolicy) ? data : undefined
	);
	if (res.kind !== 'ok') return res.kind === 'retry' && status >= 200 && status < 300 ? { mode: 'absent' } : { mode: 'unknown' };
	const d = res.data;
	return {
		mode: 'known',
		policy: {
			electionId: Number(d.electionId) || 0,
			videoPolicy: d.videoPolicy as ResultVideoPolicy,
			source: (d.source as IResultVideoPolicy['source']) ?? 'default',
			maxDurationSec: Number(d.maxDurationSec) || 180,
			maxBytes: Number(d.maxBytes) || 200 * 1024 * 1024,
			windowHours: Number(d.windowHours) || 24,
			enabled: d.enabled === true,
		},
	};
};

const isVideo = (v: unknown): v is IResultVideo => isObject(v) && typeof v.id === 'number' && typeof v.viewUrl === 'string';

const toVideo = (v: IResultVideo): IResultVideo => ({
	...v,
	chapters: Array.isArray(v.chapters) ? v.chapters : [],
});

export const toStateResult = (status: number, body: unknown): ApiResult<IResultVideoState> =>
	classifyResponse(status, body, (data) => {
		if (!isObject(data) || typeof data.windowOpen !== 'boolean') return undefined;
		return {
			current: isVideo(data.current) ? toVideo(data.current) : null,
			history: Array.isArray(data.history) ? data.history.filter(isVideo).map(toVideo) : [],
			windowClosesAt: typeof data.windowClosesAt === 'string' ? data.windowClosesAt : null,
			windowOpen: data.windowOpen,
			windowClosedReason: (data.windowClosedReason as IResultVideoState['windowClosedReason']) ?? null,
			videoPolicy: POLICIES.includes(data.videoPolicy as ResultVideoPolicy)
				? (data.videoPolicy as ResultVideoPolicy)
				: 'Optional',
		};
	});

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

const all = () => true;
const base = (electionId: number) => `/api/v1/Election/${electionId}/result-videos`;

const send = async (run: () => Promise<{ status: number; data: unknown }>) => {
	try {
		const res = await run();
		return { status: res.status, body: res.data };
	} catch {
		return { status: 0, body: undefined as unknown };
	}
};

const policyCache = new Map<number, PolicyOutcome>();

/** Cached per election for the session once known; `unknown` (offline, 5xx) is never cached. */
export const getVideoPolicy = async (electionId: number, { fresh = false } = {}): Promise<PolicyOutcome> => {
	const cached = policyCache.get(electionId);
	if (cached && !fresh) return cached;
	const raw = await send(() => videoApi.get(`${base(electionId)}/policy`, { validateStatus: all }));
	const outcome = toPolicyOutcome(raw.status, raw.body);
	if (outcome.mode !== 'unknown') policyCache.set(electionId, outcome);
	return outcome;
};

/** Test seam. */
export const resetVideoPolicyCache = () => policyCache.clear();

/** Is the video offered at all for this policy outcome? */
export const videoOffered = (
	outcome: PolicyOutcome | null | undefined
): outcome is { mode: 'known'; policy: IResultVideoPolicy } =>
	outcome?.mode === 'known' && outcome.policy.enabled && outcome.policy.videoPolicy !== 'Off';

export const getVideoState = async (electionId: number, pollingUnitCode: string): Promise<ApiResult<IResultVideoState>> => {
	const raw = await send(() =>
		videoApi.get(`${base(electionId)}/${encodeURIComponent(pollingUnitCode)}`, { validateStatus: all })
	);
	return toStateResult(raw.status, raw.body);
};

/** Opens (or, for the same `clientUploadId`, re-joins) an upload session on api.core. */
export const signVideo = async (electionId: number, body: ISignVideoRequest): Promise<ApiResult<IRelaySession>> =>
	signRelay(base(electionId), { ...body });

export const registerVideo = async (electionId: number, body: IRegisterVideoRequest): Promise<ApiResult<IResultVideo>> => {
	const raw = await send(() => videoApi.post(base(electionId), body, { validateStatus: all }));
	return classifyResponse(raw.status, raw.body, (data) => (isVideo(data) ? toVideo(data) : undefined));
};

// ---------------------------------------------------------------------------
// Chunked upload through the api.core relay
// ---------------------------------------------------------------------------

export type { ChunkOutcome, CompleteOutcome, IRelaySession, IRelaySessionState };

/** Resume state: which chunks the server already holds. */
export const getUploadSession = (electionId: number, uploadId: string): Promise<ApiResult<IRelaySessionState>> =>
	getRelaySession(base(electionId), uploadId);

/**
 * Raw PUT of chunk `index` at the session's own `chunkSize` with its exact `Content-Range`.
 * 204 → progress · no response → offline · 429 / 5xx → retry (honouring `Retry-After`) ·
 * 400 / 404 / 410 → restart (re-sign) · 403 → refused.
 */
export const uploadChunk = (
	electionId: number,
	session: IRelaySession,
	blob: Blob,
	index: number,
	onProgress?: (loadedInChunk: number) => void
): Promise<ChunkOutcome> => uploadRelayChunk(base(electionId), session, blob, index, onProgress);

/** Assemble + verify. 409 CHUNKS_MISSING → resend those · 422 HASH_MISMATCH → restart · MEDIA_REJECTED → refused. */
export const completeUpload = (electionId: number, uploadId: string): Promise<CompleteOutcome> =>
	completeRelayUpload(base(electionId), uploadId);
